import { alpha } from "@mui/material";

/**
 * Builds the react-pro-sidebar `menuItemStyles` object for the given theme and
 * collapsed state. Pure factory — memoize at the call site.
 *
 * @param {Object} theme - MUI theme
 * @param {boolean} collapsed - Whether the sidebar is collapsed
 */
export function getMenuItemStyles(theme, collapsed) {
  return {
    button: ({ active, level }) => ({
      color: active
        ? theme.palette.primary.main
        : level === 1
          ? theme.palette.mode === "dark"
            ? theme.palette.grey[400]
            : theme.palette.grey[600]
          : theme.palette.mode === "dark"
            ? theme.palette.grey[300]
            : theme.palette.grey[700],
      backgroundColor: active
        ? theme.palette.mode === "dark"
          ? alpha(theme.palette.primary.main, 0.12)
          : alpha(theme.palette.primary.main, 0.08)
        : "transparent",
      borderRadius: level === 1 ? "6px" : collapsed ? "10px" : "0 24px 24px 0",
      marginRight: collapsed ? "10px" : "12px",
      marginLeft: collapsed ? "10px" : level === 1 ? "4px" : "0",
      marginTop: level === 0 ? "2px" : "0px",
      marginBottom: level === 0 ? "2px" : "0px",
      padding: collapsed ? "10px" : undefined,
      paddingLeft: level === 1 ? "12px" : undefined,
      paddingTop: level === 1 ? "0px" : undefined,
      paddingBottom: level === 1 ? "0px" : undefined,
      justifyContent: collapsed ? "center" : undefined,
      position: "relative",
      transition: "color 0.2s ease, background-color 0.2s ease",
      fontWeight: level === 0 ? 500 : active ? 500 : 400,
      fontSize: level === 0 ? "0.875rem" : "0.8rem",
      height: level === 1 ? "32px" : "44px",
      // Active indicator bar - always present, only visible when active
      "&::before":
        !collapsed && level === 0
          ? {
              content: '""',
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: "3px",
              height: "60%",
              borderRadius: "0 4px 4px 0",
              background: active
                ? `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                : "transparent",
              transition: "background 0.2s ease",
            }
          : {},
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
      },
    }),
    icon: ({ active, level }) => ({
      color: active
        ? theme.palette.primary.main
        : theme.palette.mode === "dark"
          ? theme.palette.grey[400]
          : theme.palette.grey[600],
      transition: "color 0.2s ease",
      marginRight: collapsed ? 0 : undefined,
      fontSize: level === 1 ? "18px" : undefined,
    }),
    label: () => ({
      opacity: collapsed ? 0 : 1,
      transition: "opacity 0.2s ease",
    }),
    SubMenuExpandIcon: {
      display: "none",
    },
    subMenuContent: ({ open }) => ({
      backgroundColor: "transparent",
      marginLeft: open ? "30px" : 0,
      paddingTop: open ? "2px" : 0,
      paddingBottom: open ? "4px" : 0,
      borderLeft: open
        ? `1.5px solid ${alpha(
            theme.palette.divider,
            theme.palette.mode === "dark" ? 0.12 : 0.15
          )}`
        : "none",
    }),
  };
}

/**
 * Builds the `rootStyles` object for the Sidebar component.
 * @param {Object} theme - MUI theme
 */
export function getSidebarRootStyles(theme) {
  return {
    borderRightWidth: 0,
    "& .ps-submenu-content": {
      transition: "opacity 0.35s ease !important",
    },
    "& .ps-sidebar-container": {
      background:
        theme.palette.mode === "dark"
          ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`
          : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f5f5f5", 0.95)} 100%)`,
      backdropFilter: "blur(10px)",
      borderRight: `1px solid ${
        theme.palette.mode === "dark"
          ? alpha(theme.palette.divider, 0.1)
          : alpha(theme.palette.divider, 0.15)
      }`,
    },
  };
}
